import type { GrievanceCard as GrievanceCardData } from '../services/InstituteAdminGrievanceService';
import { getPriorityStyle, getPriorityLabel } from '../shared/helpers/statusBadge';
import TagChip from '../shared/TagChip';
import { getStudentImage } from '../../../../common/styles/Images';
import { getActionElementClass, getDomainValueForCode } from '../../../../library/Core/SmartFunctions';
import { useContext } from 'react';
import { SmartContext } from '../../../../library/Core/SmartContext';
interface Props {
    grievance: GrievanceCardData;
    onViewDetails: () => void;
    onMarkResolved: () => void;
    onAddResponse: () => void;
    onDiscard: () => void;
}

// ── Institute-admin status badge styles (separate from internal-admin colors) ─
const INST_STATUS: Record<string, { label: string; bg: string; text: string; border: string }> = {
    new:          { label: 'Pending Action', bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
    underReview:  { label: 'Under Review',   bg: '#f5f3ff', text: '#7c3aed', border: '#ddd6fe' },
    acknowledged: { label: 'Acknowledged',   bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' },
    inprogress:   { label: 'In Progress',    bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
    resolved:     { label: 'Resolved',       bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' },
    discarded:    { label: 'Discarded',      bg: '#f9fafb', text: '#6b7280', border: '#e5e7eb' },
};
const getInstStatus = (s: string) => INST_STATUS[s] ?? { label: s, bg: '#f9fafb', text: '#6b7280', border: '#e5e7eb' };

// ── Component ─────────────────────────────────────────────────────────────────
const GrievanceCard = ({ grievance, onViewDetails, onMarkResolved, onAddResponse, onDiscard }: Props) => {
    const priorityStyle  = getPriorityStyle(grievance.priority);
    const statusStyle    = getInstStatus(grievance.status);
    const hasTags        = grievance.push.tags && grievance.push.tags.length > 0;
    const { state } = useContext(SmartContext);
    return (
        <div
            className="card border rounded-3 mb-3 overflow-hidden"
            style={{ borderLeft: `4px solid ${priorityStyle.border} !important` as any }}
        >
            {/* ── Card header ──────────────────────────────────────────── */}
            <div className="card-body p-3 p-md-4">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">

                    {/* Left: avatar + info */}
                    <div className="d-flex gap-3 flex-1">
                        <a
                            href={grievance.alumni.uuid ? `/alumni/${grievance.alumni.uuid}/profile` : '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => { if (!grievance.alumni.uuid) e.preventDefault(); }}
                        >
                            <img
                                src={getStudentImage({ profilePictureFilePath: grievance.alumni.avatarUrl, genderCode: grievance.alumni.genderCode })}
                                alt={grievance.alumni.name}
                                className="rounded-circle flex-shrink-0"
                                style={{ width: 48, height: 48, objectFit: 'cover', cursor: grievance.alumni.uuid ? 'pointer' : 'default' }}
                            />
                        </a>

                        <div>
                            {/* Name + star + tier */}
                            <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                <a
                                    href={grievance.alumni.uuid ? `/alumni/${grievance.alumni.uuid}/profile` : '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-18 font-600 text-dark text-decoration-none"
                                    onClick={(e) => { if (!grievance.alumni.uuid) e.preventDefault(); }}
                                >{grievance.alumni.name}</a>
                                {grievance.alumni.tier && (
                                    <>
                                        <i className="bi bi-star-fill text-warning"></i>
                                        <span
                                            className="badge rounded-pill font-500 px-2 py-1 border"
                                            style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe' }}
                                        >
                                            {grievance.alumni.tier}
                                        </span>
                                    </>
                                )}
                            </div>

                            {/* Program · Batch */}
                            {(grievance.alumni.program || grievance.alumni.batch) && (
                                <div className="font-13 text-secondary mb-1">
                                    {[grievance.alumni.program, grievance.alumni.batch && `Batch ${grievance.alumni.batch}`]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </div>
                            )}

                            {/* Grievance Type */}
                            <div className="font-13 text-secondary mb-1">
                                <span className="font-500 text-dark">Grievance Type:</span>{' '}
                                {getDomainValueForCode(grievance.category, 'GRIEVANCE_CATEGORY', state) || grievance.category}
                            </div>

                            {/* ID + Received */}
                            <div className="font-13 text-secondary mb-2">
                                <span className="font-500 text-dark">ID:</span> {grievance.code}
                                &nbsp;·&nbsp;
                                <span className="font-500 text-dark">Received:</span> {grievance.receivedDate}
                            </div>

                            {/* Importance tag chips */}
                            {hasTags && (
                                <div className="d-flex flex-wrap gap-1">
                                    {grievance.push.tags.map((tag) => (
                                        <TagChip key={tag.tagCode} tag={tag} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right: priority + status badges */}
                    <div className="d-flex flex-wrap gap-2">
                        <span
                            className="badge rounded-pill font-500 px-3 py-2 border"
                            style={{
                                backgroundColor: priorityStyle.bg,
                                color: priorityStyle.text,
                                borderColor: priorityStyle.badgeBorder,
                            }}
                        >
                            {grievance.priority === 'critical' && <span className="me-1">●</span>}
                            {getPriorityLabel(grievance.priority)} Priority
                        </span>
                        <span
                            className="badge rounded-pill font-500 px-3 py-2 border"
                            style={{
                                backgroundColor: statusStyle.bg,
                                color: statusStyle.text,
                                borderColor: statusStyle.border,
                            }}
                        >
                            {statusStyle.label}
                        </span>
                    </div>
                </div>
            </div>

            {/* ── Team Notes ───────────────────────────────────────────── */}
            {grievance.push.teamNotes && (
                <div className="border-top px-3 px-md-4 py-3">
                    <div className="font-12 fw-600 text-secondary text-uppercase mb-2">
                        <i className="bi bi-building me-1"></i>Univariety Team Notes
                    </div>
                    <div className="font-14 text-dark">{grievance.push.teamNotes}</div>
                </div>
            )}

            {/* ── Suggested Resolution ─────────────────────────────────── */}
            {grievance.push.suggestedResolution && (
                <div className="border-top px-3 px-md-4 py-3 bg-success bg-opacity-10">
                    <div className="font-12 fw-600 text-success text-uppercase mb-2">
                        <i className="bi bi-lightbulb-fill me-1"></i>Suggested Resolution
                    </div>
                    <div className="font-14 text-success">{grievance.push.suggestedResolution}</div>
                </div>
            )}

            {/* ── Action bar ───────────────────────────────────────────── */}
            <div
                className="border-top px-3 px-md-4 py-3 d-flex flex-wrap justify-content-between align-items-center gap-2"
                style={{ backgroundColor: '#f9fafb' }}
            >
                <div className="d-flex flex-wrap gap-2">
                    <button className="btn btn-border-gray px-3" onClick={onViewDetails}>
                        <span>View Full Details</span>
                    </button>
                    {grievance.status !== 'resolved' && grievance.status !== 'discarded' && (
                        <button className={`btn btn-green px-3 height-35 font-400 ${getActionElementClass()}`} onClick={onMarkResolved}>
                            <i className="bi bi-check-all font-20 me-1 position-relative"></i>
                            <span className="font-400">Mark as Resolved</span>
                        </button>
                    )}
                    {grievance.status !== 'resolved' && grievance.status !== 'discarded' && (
                        <button className={`btn btn-darkblue height-35 px-3 font-400 ${getActionElementClass()}`} onClick={onAddResponse}>
                            <i className="bi bi-chat-dots-fill font-16 me-1 position-relative"></i>
                            <span className="font-400">Add Response</span>
                        </button>
                    )}
                    {grievance.status !== 'resolved' && grievance.status !== 'discarded' && (
                        <button className={`btn btn-border-gray px-3 ${getActionElementClass()}`} onClick={onDiscard}>
                            <i className="bi bi-x me-1 position-relative"></i>
                            <span>Discard</span>
                        </button>
                    )}
                </div>
                <div className="font-12 text-secondary">Last updated: {grievance.updatedAt}</div>
            </div>
        </div>
    );
};

export default GrievanceCard;
